var logError = require("./error.js");
var Collections = require("./src/collections/index.js");
var GuiEvent = require("./event.js").GuiEvent;
var GuiEvents = require("./event.js").GuiEvents;
var logError = require("./error.js");
var inherit = require("./src/general.js").inherit;
/**
 *  Creates an instance of a Statemachine:
 *  It contains the current state, a list of possible states and transitions and a list of appended sub statemachines.
 *  You can set this.DEBUG = true to show verbose debugging information in the console: For each activated transition it will show the event, starting state, resulting state and the transition object.
 *
 * @param {value} [viewState] If a viewstate is passed, it will duplicate the state of this statemachine, which can be observed
 * @param [circuitsRecorder] The recorder object to record handled events
 * @param [scope] Defines an event scope for this statemachine
 * @constructor
 *
 * @tutorial statemachine
 */
var StateMachine = function(viewState, circuitsRecorder, scope) {
    this.recorder = null;

    this._state = "idle";
    this.state = function(value) {
        if (value !== undefined) {
            this._state = value;
        } else {
            return this._state;
        }
    };

    this.viewState = viewState;
    if (viewState) viewState(this.state());

    if (circuitsRecorder) {
        this.recorder = circuitsRecorder;
        this.recorder.setStatemachine(this);
    }

    this.scope  = scope || "";  // by default, the scope contains the entire statemachine

    this.parent = null;                                 // parent statemachine to wich this is merged
    this.appendedSM = new Collections.HashMap();        // Hashmap of sub statemachines appended to this statemachine
    this.regExpAppends = new Collections.HashMap();     // Collections of appended SM with regular expression

    this.stateCollection = new Collections.HashMap();   // Hashmap of states: Each state has a set of outgoing and a set of incoming transistions

    this.transitionIdx = 0;                             // automatic counter for anonomous transitions
    this.transitionList = new Collections.HashMap();    // List of all transition in this statemachine
    this.regExpTransitions = new Collections.HashSet(); // Collection of regular expressions that can add transitions automatically if the from state matches the regExp.

    this.eventActionIdx = 0;                            // automatic counter for anonomous event actions
    this.eventActions = new Collections.HashMap();      // List of event actions in this statemachine
    this.regExpEventActions = new Collections.HashSet();

    this.eventQueue = new Collections.Fifo();

    // if events array defined on constructor, add them to GuiEvents
    if (this.constructor.guiEvents)
      GuiEvents.addEvents(this.constructor.guiEvents);

    this.DEBUG = false;  // default debug state
};
StateMachine.DEBUG = false;
StateMachine.CIRCULAR = "_CircularTransition_"; // constant that can be used to indicate you want to add a circular transition (from state = to state)

module.exports = StateMachine;

/**
 * State Object with collections of connected transistions, for internal use only.
 *
 * @param {StateMachine} sm The statemachine to which this state belongs
 * @param {String} state The name of the state
 * @constructor
 */
StateMachine.State = function(sm, state) {
    this.name = state;          // this state name can be extended when the statemachine is appended

    this.originalName = state;  // original name of state when created
    this.originalSM  = sm;      // original statemachine where this state is created

    this.outTransitions = new Collections.HashSet(); // Hashset of transitions starting from this state
    this.inTransitions = new Collections.HashSet();  // Hashset of transitions pointing to this state
    this.eventActions = new Collections.HashSet();   // Hashset of eventActions for this state
};



StateMachine.prototype.getStateObj = function(state) { // returns the state-object with transistions, if state does not exist yet, makes new one

    if (state.constructor == StateMachine.State)
        return state;

    var stateObj = this.stateCollection.get(state);
    if (!stateObj) {
        stateObj = new StateMachine.State(this, state);
        this.addStateObj(stateObj);
    }
    return stateObj;
};

// for internal use only: add a new state to the collection and checks all the reg exp transitions to see if extra transistions or event actions needs to be added.
StateMachine.prototype.addStateObj = function(state){
    this.stateCollection.put(state.name, state);
    this.regExpTransitions.forEach(function(regExpTransition){
        regExpTransition.checkState(state);
    });
    this.regExpEventActions.forEach(function(regExpEventAction){
        regExpEventAction.checkState(state);
    });
    this.regExpAppends.forEach(function(key, regExpAppend){
        regExpAppend.checkState(state);
    });
};

/**
 * Sets the state of the current statemachine.
 * @param {(State | String)} state Resulting state
 */
StateMachine.prototype.setState = function(state) {
    var name = (state.constructor == StateMachine.State) ? state.name : state;
    console.assert(this.stateCollection.get(name), "state not known");
    // console.assert(this.parent, "setState only possible on root statemachine");
    this.state(name);
    if (this.viewState) this.viewState(name);
};


StateMachine.prototype.state_eq = function(other) {
    if (other.constructor == StateMachine.State) other = other.name;
    return this.state() == other;
};

/**
 * Removes a state out of this statemachine, togheter with all its incoming and outgoing transistions.
 * Update: now you can remove states of a statemachine that is already merged.
 *
 * @param {(State | String)} state_to_remove State to remove
 */
StateMachine.prototype.removeState = function(state_to_remove) {    // TODO: If you remove in a parent SM you  a state that comes from an appended subSM: call the remove of that state in its original SM, than it will bubble up.

    var state = null;
    if (state_to_remove.constructor == StateMachine.State) state = state_to_remove;
    else if (this.stateCollection.contains(state_to_remove)) state = this.getStateObj(state_to_remove);

    if (state) {
        // remove state out of Collection
        this.stateCollection.removeValue(state);

        // remove all outgoing transistions:
        state.outTransitions.forEach(function(transistion){
            transistion.remove();
        });

        // remove all incoming transistions:
        state.inTransitions.forEach(function(transistion){
            transistion.remove();
        });

        // remove all eventActions:
        state.eventActions.forEach(function(eventAction){
            eventAction.remove();
        });

        // recursively remove states out of parentSM
        if (this.parent) this.parent.removeState(state);
    }
};

/**
 * Transition object.
 *
 * @param {Statemachine} sm Statemachine it belongs to
 * @param {State} from Start state
 * @param {State} to Resulting state
 * @param {function(): boolean} condition Function that is called to determine if this transition is activated by a given a cEvent
 * @param {(function() | Array)} action A function or an array of functions that will be called when this transition is activated
 * @param {String} [id] A unique id to search for a transition. This id is also used by the debug information
 * @param {String} [scope] Scope for this transition: this can limit the amount of events it will react on. By default transition has the scope of the original statemachine
 * @constructor
 */
StateMachine.Transition = function(sm, from, to, condition, action, id, scope) {
    var self = this;
    this.originalSM = sm;

    this.from = sm.getStateObj(from);
    this.from.outTransitions.add(this);

    this.to = (to == StateMachine.CIRCULAR) ? this.from : sm.getStateObj(to);
    this.to.inTransitions.add(this);

    this.condition = condition;
    this.scope = (scope != undefined) ? scope : this.originalSM.scope;

    this.actions = (action && action.constructor == Array) ? action : [action]; // ordered list of actions to execute when transition is active

    if (this.originalSM.viewState) { // Change viewState of original SM
        var toState = this.to.originalName; // necessary to create a closure var to the original to state (since self.to can change while appending)
        this.actions.push(function(){
            self.originalSM.viewState(toState);
        });
    }

    this.id = id || "_" + sm.transitionIdx++;
    console.assert(!sm.transitionList.contains(this.id), "'" + this.id + "' transition already exist in stateMachine");
    sm.transitionList.put(this.id, this);
};

/**
 * Adds a function in front of the action list of this transition.
 * @param {function()} action Action function to be added
 */
StateMachine.Transition.prototype.addActionBefore = function(action) {
    if (action) this.actions.unshift(action);
};

/**
 * Adds a function at the end of the action list of this transition.
 * @param {function()} action Action function to be added
 */
StateMachine.Transition.prototype.addActionAfter = function(action) {
    if (action) this.actions.push(action);
};

StateMachine.Transition.prototype.isActive = function(cEvent) {

   return cEvent.scope().match(this.scope.substring(0, cEvent.scope().length)) && this.condition(cEvent);
};

StateMachine.Transition.prototype.execute = function(cEvent) {

    // execute transition actions
    for (var i in this.actions){
        var action = this.actions[i];
        if (action) action(cEvent);
    }
};

StateMachine.Transition.prototype.remove = function() {
    if (this.to) this.to.inTransitions.remove(this);
    this.to = null;

    if (this.from) this.from.outTransitions.remove(this);
    this.from = null;

    this.originalSM.transitionList.remove(this.id);
};

StateMachine.Transition.prototype.reMap = function(from, to) {
    if (from && from != this.from) {
        this.from = from;
        from.outTransitions.add(this);
    }
    if (to && to != this.to) {
        this.to = to;
        to.inTransitions.add(this);
    }
    // Attention: This function does not remove this transition out of original from / to states Collection (this info is still necessary if you want to remove a certain SM)
};

StateMachine.prototype.getTransition = function(id){
    return this.transitionList.get(id);
};

/**
 * Regular Expression Transition object.
 *
 * @param {Statemachine} sm Statemachine it belongs to
 * @param {RegExp} regex Regular expression: this transition is added to each state of this and appended statemachines that matches the regexp
 * @param {State} to Resulting state. Use StateMachine.CIRCULAR to indicate you want to add a circular transition (from state = to state)
 * @param {function(): boolean} condition Function that is called to determine if this transition is activated by a given a cEvent
 * @param {(function() | Array)} action A function or an array of functions that will be called when this transition is activated
 * @param {String} [id] A unique id to search for a transition. This id is also used by the debug information
 * @param {String} [scope] Scope for this transition: this can limit the amount of events it will react on. By default transition has the scope of the original statemachine
 * @constructor
 */
StateMachine.RegExpTransition = function(sm, regex, to, condition, action, id, scope){
    var self = this;

    this.originalSM = sm;
    this.regex = regex;
    this.to = to;
    this.condition = condition;
    this.scope = scope;
    this.action = action;
    this.id = id || "_regEx_" + sm.transitionIdx++;

    this.nr = 0;

    this.addedTransitions = new Collections.HashSet(); // collection of transitions added by this regexp transition

    this.originalSM.regExpTransitions.add(this);

    sm.stateCollection.forEach(function(name,state){
        self.checkState(state);
    });
};

StateMachine.RegExpTransition.prototype.checkState = function(state){
    if (state.name.match(this.regex)){
        var transition = new StateMachine.Transition(this.originalSM, state, this.to, this.condition, this.action, this.id + "_" + this.nr++, this.scope); // create a transition for each existing state that matches the reg expression
        this.addedTransitions.add(transition);
    }
};

/**
 * Removes a regexp transition and all transitions added by this regexp transition
 */
StateMachine.RegExpTransition.prototype.remove = function(){
    this.addedTransitions.forEach(function(transition){
       transition.remove();
    });
    this.addedTransitions.removeAll();
    this.originalSM.regExpTransitions.remove(this);
};

/**
 * Adds a function in front of the action list of this transition.
 * @param {function()} action Action function to be added
 */
StateMachine.RegExpTransition.prototype.addActionBefore = function(action) {
    this.addedTransitions.forEach(function(transition){
        transition.addActionBefore(action);
    });
};

/**
 * Adds a function at the end of the action list of this transition.
 * @param {function()} action Action function to be added
 */
StateMachine.RegExpTransition.prototype.addActionAfter = function(action) {
    this.addedTransitions.forEach(function(transition){
        transition.addActionAfter(action);
    });
};

/**
 * Adds a transition to the this statemachine.
 * Note from can also be a regular expression. In this case this transition is added to each state of this and appended statemachines that matches the regexp.
 * The reg exp is applied to the extended name of possible appended statemachine-states.
 * If you want to add a circular transtions with a reg exp us StateMachine.CIRCULAR as to argument.
 *
 * @param {(State | String | RegExp)} from Starting state. This can be a regular expression.
 * @param {(State | String)} to Ending state. Use StateMachine.CIRCULAR to indicate you want to add a circular transition (from state = to state)
 * @param {function(): boolean} condition Function that is called to determine if this transition is activated by a given a cEvent
 * @param {(function() | Array)} action A function or an array of functions that will be called when this transition is activated
 * @param {String} [id] A unique id to search for a transition. This id is also used by the debug information
 * @param {String} [scope] Scope for this transition: this can limit the amount of events it will react on. By default transition has the scope of the original statemachine
 * @returns {Transition} The transition object
 */

StateMachine.prototype.addTransition = function(from, to, condition, action, id, scope) { // TODO: make add Transistion possible in already merged SM

    console.assert(!this.parent, "This statemachine is already merged: unable to add transition"); // editing of already merged SM is prohibited

    if (from.constructor == RegExp){
        var transition = new StateMachine.RegExpTransition(this, from, to, condition, action, id, scope);
    } else {
        var transition = new StateMachine.Transition(this, from, to, condition, action, id, scope);
    }

    return transition;
};

/**
 * Removes a transition out of this statemachine.
 * @param {(Transition | String) } transition The transiton object (or its id) to be removed
 */
StateMachine.prototype.removeTransition = function(transition) {    // TODO: if you remove a transition that comes from an appended subSM recursively remove transition
    if (transition.constructor == StateMachine.Transition) transition.remove();
    else {
        var trans = this.transitionList.get(transition);
        if (trans) trans.remove();
    }
};

/**
 * EventAction object.
 *
 * @param {Statemachine} sm Statemachine it belongs to
 * @param {State} from Start state
 * @param {function(): boolean} condition Function that is called to determine if this transition is activated by a given a cEvent
 * @param {(function() | Array)} action A function or an array of functions that will be called when this transition is activated
 * @param {String} [id] A unique id to search for a transition. This id is also used by the debug information
 * @param {String} [scope] Scope for this transition: this can limit the amount of events it will react on. By default transition has the scope of the original statemachine
 * @constructor
 */
StateMachine.EventAction = function(sm, from, condition, action, id, scope) {
    var self = this;
    this.originalSM = sm;

    this.from = sm.getStateObj(from);
    this.from.eventActions.add(this);

    this.condition = condition;
    this.scope = (scope != undefined) ? scope : this.originalSM.scope;

    this.actions = (action && action.constructor == Array) ? action : [action]; // ordered list of actions to execute when transition is active

    this.id = id || "_" + sm.eventActionIdx++;
    console.assert(!sm.eventActions.contains(this.id), "'" + this.id + "' eventAction already exist in stateMachine");
    sm.eventActions.put(this.id, this);
};
inherit(StateMachine.EventAction, StateMachine.Transition);

StateMachine.EventAction.prototype.remove = function() {
    if (this.from) this.from.eventActions.remove(this);
    this.from = null;
};

StateMachine.EventAction.prototype.reMap = function(from) {
    if (from && from != this.from) {
        this.from = from;
        from.eventActions.add(this);
    }
    // Attention: This function does not remove this eventAction out of original from states Collection (this info is still necessary if you want to remove a certain SM)
};

StateMachine.prototype.getEventAction = function(id){
    return this.eventActions.get(id);
};

/**
 * Regular Expression EventAction object.
 *
 * @param {Statemachine} sm Statemachine it belongs to
 * @param {RegExp} regex Regular expression: this transition is added to each state of this and appended statemachines that matches the regexp
 * @param {function(): boolean} condition Function that is called to determine if this transition is activated by a given a cEvent
 * @param {(function() | Array)} action A function or an array of functions that will be called when this transition is activated
 * @param {String} [id] A unique id to search for a transition. This id is also used by the debug information
 * @param {String} [scope] Scope for this transition: this can limit the amount of events it will react on. By default transition has the scope of the original statemachine
 * @constructor
 */
StateMachine.RegExpEventAction = function(sm, regex, condition, action, id, scope){
    var self = this;

    this.originalSM = sm;
    this.regex = regex;
    this.condition = condition;
    this.scope = scope;
    this.action = action;
    this.id = id;

    this.nr = 0;

    this.addedEventActions = new Collections.HashSet(); // collection of transitions added by this regexp transition

    this.originalSM.regExpEventActions.add(this);

    sm.stateCollection.forEach(function(name,state){
        self.checkState(state);
    });
};

StateMachine.RegExpEventAction.prototype.checkState = function(state){
    if (state.name.match(this.regex)){
        var transition = new StateMachine.EventAction(this.originalSM, state, this.condition, this.action, ((this.id) ? this.id + this.nr++ : null), this.scope); // create a transition for each existing state that matches the reg expression
        this.addedEventActions.add(transition);
    }
};

/**
 * Removes a regexp transition and all transitions added by this regexp eventAction
 */
StateMachine.RegExpEventAction.prototype.remove = function(){
    this.addedEventActions.forEach(function(eventAction){
        eventAction.remove();
    });
    this.addedEventActions.removeAll();
    this.originalSM.regExpEventActions.remove(this);
};

/**
 * Adds a function in front of the action list of this eventAction.
 * @param {function()} action Action function to be added
 */
StateMachine.RegExpEventAction.prototype.addActionBefore = function(action) {
    this.addedEventActions.forEach(function(eventAction){
        eventAction.addActionBefore(action);
    });
};

/**
 * Adds a function at the end of the action list of this eventAction.
 * @param {function()} action Action function to be added
 */
StateMachine.RegExpEventAction.prototype.addActionAfter = function(action) {
    this.addedEventActions.forEach(function(eventAction){
        eventAction.addActionAfter(action);
    });
};

/**
 * Adds an eventAction to this statemachine.
 * Note from can also be a regular expression. In this case this eventAction is added to each state of this and appended statemachines that matches the regexp.
 * The reg exp is applied to the extended name of possible appended statemachine-states.
 *
 * @param {(State | String | RegExp)} from Starting state. This can be a regular expression.
 * @param {function(): boolean} condition Function that is called to determine if this eventAction is activated by a given a cEvent
 * @param {(function() | Array)} action A function or an array of functions that will be called when this transition is activated
 * @param {String} [id] A unique id to search for a eventAction. This id is also used by the debug information
 * @param {String} [scope] Scope for this transition: this can limit the amount of events it will react on. By default transition has the scope of the original statemachine
 * @returns {Transition} The eventAction object
 */

StateMachine.prototype.addEventAction = function(from, condition, action, id, scope) {

    console.assert(!this.parent, "This statemachine is already merged: unable to add EventAction"); // editing of already merged SM is prohibited

    if (from.constructor == RegExp){
        var eventAction = new StateMachine.RegExpEventAction(this, from, condition, action, id, scope);
    } else {
        var eventAction = new StateMachine.EventAction(this, from, condition, action, id, scope);
    }

    return eventAction;
};

/**
 * Removes a transition out of this statemachine.
 * @param {(Transition | String) } transition The transiton object (or its id) to be removed
 */
StateMachine.prototype.removeEventAction = function(eventAction) {
    if (eventAction.constructor == StateMachine.EventAction) {
        eventAction.remove();
    }
    else {
        var trans = this.eventActions.get(eventAction);
        if (trans) trans.remove();
    }
    if (eventAction.id) this.eventActions.remove(eventAction.id);
};

/**
 * Append a certain sub statemachine to a specified state in this statemachine. More information can be found in the tutorial: {@tutorial statemachine}.
 *
 * @param {String} key Key used to identify the sub statemachine
 * @param {StateMachine} statemachine The sub statemachine object that is merged
 * @param {(State | String)} enter Enter state of parent statemachine: all outgoing transitions of the mergestate of the substatemachine will start from this state in the resulting statemachine
 * @param {function()} enter_func Function that is added in front of the action list for all transitions starting from the merge state of the sub statemachine
 * @param {(State | String)} exit  Exit state of parent statemachine: all incoming transitions of the mergestate of the substatemachine will point to this state in the resulting statemachine
 * @param {function()} exit_func  Function that is added at the end of the action list for all transitions pointing to the merge state of the sub statemachine
 * @param {(State | String)} [merge=idle] State of the substatemachine that is merged. If this argument is missing, it will merge the idle state by default
 */
StateMachine.prototype.append = function(key, statemachine, enter, enter_func, exit, exit_func, merge) {
    var self = this;

    if (enter.constructor == RegExp){   // if this is a regEx append: create RegExpAppend object who will handle all the necessary appends
        console.assert(typeof statemachine == "function", "RegExp append expects a statemachine creator function instead of a statemachine instance!"); // editing of already merged SM is prohibited
        var regExpAppend = new StateMachine.RegExpAppend(this, key, statemachine, enter, enter_func, exit, exit_func, merge);
        this.regExpAppends.put(key, regExpAppend);
        return;
    }

    console.assert(!statemachine.parent, "This statemachine is already merged: unable to merge again"); // editing of already merged SM is prohibited
    console.assert(!this.appendedSM.contains(key), "There is already a substatemachine appended with key '" + key + "'");

    if (!merge) merge = 'idle'; // by default: merge with idle state of subSM
    var mergeState = statemachine.getStateObj(merge);
    var enterState = this.getStateObj(enter);
    var exitState = (exit == StateMachine.CIRCULAR) ? enterState : this.getStateObj(exit);

    statemachine.parent = this;
    statemachine.mergeState = mergeState;

    this.appendedSM.put(key, statemachine);

    // add all states (except for mergeState) of substatemachine to this statemachine with key as prefix:
    statemachine.stateCollection.forEach(function(name, state){
        if (state != mergeState) {
            state.name = key + " - " + state.name;
            self.addStateObj(state);
        }
    });

    // add right actions to the transitions of this state and remap transition
    mergeState.outTransitions.forEach(function(transition){
        transition.addActionBefore(enter_func);    // add enter function to all outgoing transitions of mergeState
        transition.reMap(enterState,null);
    });

    mergeState.inTransitions.forEach(function(transition){
        transition.addActionAfter(exit_func);     // add exit function to all incoming transitions of mergeState
        transition.reMap(null,exitState);
    });

    // add right actions to the eventActions of this state and remap transition
    mergeState.eventActions.forEach(function(eventAction){
        eventAction.addActionBefore(enter_func);    // add enter function to all outgoing transitions of mergeState
        eventAction.reMap(enterState);
    });

    // add all transitions to this statemachine with key as prefix
    statemachine.transitionList.forEach(function(id, tran){
        self.transitionList.put(key + " - " + id, tran);

        if (self.viewState) { // Change viewState of this SM
            var toState = tran.to.name; // necessary to create a closure var to the current to state (since self.to can change while following appendings)
            tran.addActionAfter(function(){
                self.viewState(toState);
            });
        }
    });

    // TODO: maybe also add eventActions to this statemachine

};

/**
 * This function will remove a certain subSM out of the current statemachine. It will remove all states and the corresponding transitions.
 * You are able to remove a subSM out of an already merged statemachine.
 * Note that this process will also destroy the current instance of the subSM you want to remove. If you would have save a reference to the instance of the subSM, you can't reuse
 * this instance since all states and transitions will be removed.
 * @param {String} key Key of the substatemachine you want to remove
 */
StateMachine.prototype.removeSubSM = function(key){
    var self = this;

    var subSM = this.appendedSM.get(key);
    if (subSM){
        var mergeState = subSM.mergeState;

        // remove all states of subSM in parent SM
        subSM.stateCollection.forEach(function(originalName, state){
            if (state != mergeState){
                self.removeState(state);
            }
        });

        // remove remapped transitions to mergeState
        mergeState.outTransitions.forEach(function(transition){
            transition.remove();    // remove remapped enter transitions to the subSM out of the parent SM
        });

        mergeState.inTransitions.forEach(function(transition){
            transition.remove();    // remove remapped exit transitions to the subSM out of the parent SM
        });
        // note that the previous actions will destroy the current instance of the subSM, since all (but the mergeState) states and transitions will be removed, so you can't reuse this instance after you have removed it out of a parentSM

        mergeState.eventActions.forEach(function(eventAction){
            eventAction.remove();    // remove remapped eventAction to the subSM out of the parent SM
        });

        // remove subSM out of appendedSM collection
        this.appendedSM.remove(key);
        subSM.parent = null;
    }

    var regExpAppend = this.regExpAppends.get(key);
    if (regExpAppend) { // remove regExp append
        regExpAppend.remove();
        this.regExpAppends.remove(key);
    }
};


StateMachine.RegExpAppend = function(sm, key, statemachineCreator, enterRegExp, enter_func, exit, exit_func, merge){
    var self = this;

    this.originalSM = sm;

    this.key = key;
    this.statemachineCreator = statemachineCreator;
    this.enterRegExp = enterRegExp;
    this.enter_func = enter_func;
    this.exit = exit;
    this.exit_func = exit_func;
    this.merge = merge;

    this.nr = 0;

    sm.stateCollection.copy().forEach(function(name,state){
        self.checkState(state);
    });
};

StateMachine.RegExpAppend.prototype.checkState = function(state){
    if (state.name.match(this.enterRegExp) && state.originalSM != this.newSubSM){
        this.newSubSM = this.statemachineCreator()
        this.originalSM.append("_" + this.key + "_" + this.nr++, this.newSubSM, state, this.enter_func, this.exit, this.exit_func, this.merge);
    }
};

StateMachine.RegExpAppend.prototype.remove = function(){
    for (var i =0; i<this.nr; i++){
        this.originalSM.removeSubSM("_" + this.key + "_" + i);
    }
};

/**
 * This function handles the events: It calls the condition function for every outgoing transition of the current state of the statemachine.<br>
 * If there is exactly 1 active transition, it will execute the according action list and change the state.<br>
 * If this function is called on a sub statemachine, it will automatically send the event to the parent SM.<br>
 * It also schedules events in a FIFO queue: so you can schedule new events during the handling of a previous event.
 *
 * @param {GuiEvent} cEventOrType cEvent object to be handled
 * @returns {boolean}
 */
StateMachine.prototype.handle = function(cEventOrType) {
    // handles a cEvent or if only a type is given, make a new cEvent and handle that one
    var cEvent = (cEventOrType.constructor == GuiEvent) ? cEventOrType : new GuiEvent(cEventOrType);

    if (this.recorder) {
        this.recorder.record(cEvent, this.editor);
    }

    if (this.recorder && this.recorder.playing) {
        // Block injected events.
        return;
    }

    if (this.parent){ // check if this is topLevel SM, if not: inject event in parent statemachine
        this.parent.handle(cEvent);
    } else {
        this.eventQueue.enqueue(cEvent);// Add event to queue

        if (!this.busy){
            this.busy = true;
            while (this.eventQueue.size() > 0) {
                cEvent = this.eventQueue.dequeue();
                this.handleTransition(cEvent);
            }
            this.busy = false;
        }
    }

    return false;
};


StateMachine.prototype.handleTransition = function(cEvent) {
    var self = this;

    // current state
    var state = this.stateCollection.get(this.state());
    console.assert(state, "state unknown");
    if(!state) return;

    // search all active eventActions from this state
    var activeEventActions = [];
    state.eventActions.forEach(function(eventAction){
        if (eventAction.isActive(cEvent)) activeEventActions.push(eventAction);
    });

    // search all active transitions from this state
    var activeTransitions = [];
    state.outTransitions.forEach(function(transition){
        if (transition.isActive(cEvent)) activeTransitions.push(transition);
    });

    // check if only one transition is active
    console.assert(activeTransitions.length <= 1, "More than 1 active transition!!!!");

    // execute
    try{
        for (var i in activeEventActions){  // execute active event actions
            activeEventActions[i].execute(cEvent);
        }
        if (activeTransitions.length == 1) {    // execute active transitions
            activeTransitions[0].execute(cEvent);
            this.setState(activeTransitions[0].to); // change state
        }
        if(activeTransitions.length != 0 || activeEventActions.length != 0) {
            this.afterHandle(cEvent);   // execute afterhandle if something has happened
        }
    } catch(err) {
        logError("StateMachine",err,logError.criticality.CRITICAL);
        try{
            this.root.cancelRec();
        } catch(err2) {
            logError("StateMachine CancelRec",err2);
        }
        var recorderIsPlaying = this.recorder && this.recorder.playing;
        if (StateMachine.DEBUG || this.DEBUG || recorderIsPlaying) throw(err);   // if in debug mode rethrow error
    }

    // for debugging only:
    if (activeTransitions.length == 0) return;
    if (activeTransitions.length > 1 || StateMachine.DEBUG || activeTransitions[0].originalSM.DEBUG){
        if (this.prevTransition && activeTransitions[0] == this.prevTransition[0] && activeTransitions[1] == this.prevTransition[1]) console.log("same transition");
        else {
            for (var i = 0; i<activeTransitions.length; i++) {
                console.log(i, cEvent.type().name || cEvent.type(), "From: '" + activeTransitions[i].from.name + "' To: '" + activeTransitions[i].to.name + "'" ,"in: " + activeTransitions[i].id); //, activeTransitions[i]);
            }
            for (var i = 0; i<activeEventActions.length; i++) {
                console.log(i, cEvent.type().name || cEvent.type(), "From: '" + activeEventActions[i].from.name + "' " ,"in: " + activeEventActions[i].id); //, activeEventActions[i]);
            }
        }
        this.prevTransition = activeTransitions;
    }
};

/**
 * This function is called every time a transition is executed. Must be overridden by the top level statemachine.
 */
StateMachine.prototype.afterHandle = function(cEvent) {
};

StateMachine.prototype.defineGuiEvents = function(events) {
    for (var event in events) {
        // !!! TODO: if statement should be made active again
        //if (!GuiEvents[event]){
            GuiEvents[event] = events[event];
        //}
    }
};


StateMachine.prototype.createJSON = function(){
    var sm = {nodes: {}, links: []};

    this.stateCollection.forEach(function(name, state){
        var parts = name.split(" - ");
        var showName = parts[parts.length-1];
        sm.nodes[name] = {name: showName, group:parts.length};

        state.outTransitions.forEach(function(trans){
            sm.links.push({source: trans.from.name, target: trans.to.name, value:trans.id});
        });
        state.eventActions.forEach(function(eventAction){
            sm.links.push({source: eventAction.from.name, value:eventAction.id});
        });
    });

    return JSON.stringify(sm);
};




